import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { type IResponseFullCampaign } from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { QuietHoursSettingsStore } from 'entities/QuietHours'
import { CampaignSettingsUi } from '../ui/CampaignSettingsUi'

export class CampaignSettingsStore {
  private _onReEnterChange?: (value: boolean) => void
  private readonly _quietHoursSettings: QuietHoursSettingsStore

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._quietHoursSettings = new QuietHoursSettingsStore(_entities)
  }
  modalId = nanoid()
  isUnEnroll = false
  isReEnter = false
  isQuiteHours = false

  setOnReEnterChange = (callback: (value: boolean) => void) => {
    this._onReEnterChange = callback
  }

  get params() {
    return {
      unenroll_on_response: this.isUnEnroll,
      allow_re_entering: this.isReEnter,
      enable_quiet_hours: this.isQuiteHours,
    }
  }

  init = (params?: Partial<IResponseFullCampaign['settings']> | null) => {
    this.isUnEnroll = params?.unenroll_on_response ?? false
    this.isReEnter = params?.allow_re_entering ?? false
    const hasSavedQuietHours = params?.enable_quiet_hours !== undefined
    this.isQuiteHours = hasSavedQuietHours
      ? Boolean(params.enable_quiet_hours)
      : Boolean(this._entities.organizationStore.quietHoursSettings?.is_active)
  }

  openModal = () => {
    modalStore.addModal({
      id: this.modalId,
      width: 540,
      position: 'right',
      type: ModalTypeList.DEFAULT,
      disabledOverlayClose: true,
      pureContent: true,
      title: 'Settings',
      fullHeight: true,
      ModalContent: () => <CampaignSettingsUi store={this} />,
    })
  }

  onSave = ({
    isUnEnroll,
    isReEnter,
    isQuiteHours,
  }: {
    isUnEnroll: boolean
    isReEnter: boolean
    isQuiteHours: boolean
  }) => {
    if (isUnEnroll !== this.isUnEnroll) {
      toastStore.add({
        type: 'info',
        title: `Contacts will ${isUnEnroll ? '' : 'not'} be unenrolled upon reply`,
      })
    }
    if (isReEnter !== this.isReEnter) {
      toastStore.add({
        type: 'info',
        title: `Contacts can ${isReEnter ? '' : 'not'} be allowed to re-enter campaign`,
      })
    }
    if (isQuiteHours !== this.isQuiteHours) {
      toastStore.add({
        type: 'info',
        title: 'Settings saved',
      })
    }
    this.isUnEnroll = isUnEnroll
    this.setIsReEnter(isReEnter)
    this.isQuiteHours = isQuiteHours

    modalStore.closeModal(this.modalId)
  }

  onCancel = () => {
    modalStore.closeModal(this.modalId)
  }

  setIsReEnter = (value: boolean) => {
    this.isReEnter = value
    this._onReEnterChange?.(value)
  }

  get isQuietHoursEnabled() {
    return this._quietHoursSettings.isQuietHoursEnabled
  }

  get isShowQuietHours() {
    return this._quietHoursSettings.isShowQuietHours
  }

  get isQuietHoursBlockOnWeekend() {
    return this._quietHoursSettings.isQuietHoursBlockOnWeekend
  }

  get quietHoursAllowedTimeFrom() {
    return this._quietHoursSettings.quietHoursAllowedTimeFrom
  }

  get quietHoursAllowedTimeTo() {
    return this._quietHoursSettings.quietHoursAllowedTimeTo
  }

  dispose = () => {
    this._quietHoursSettings.dispose()
  }
}
